import React from 'react';
import { StateScreen } from './ui';

/**
 * React error boundary that catches render-time errors and shows a
 * StateScreen instead of a white page or component stack. Two variants:
 *
 *   <ErrorBoundary>...</ErrorBoundary>           — full-page (default)
 *   <ErrorBoundary variant="in-chrome">          — keeps chrome above
 *
 * Mount at:
 *   • The route layer (full-page) — catches errors in route components.
 *   • Each tab body (in-chrome) — catches errors inside a single tab
 *     without nuking the surrounding project chrome.
 *
 * Error boundaries only catch RENDER errors and lifecycle errors in
 * descendants. Async failures (failed fetch, thrown promises) still
 * need explicit handling — surface those by setting state and rendering
 * a StateScreen directly.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to console for now; future: ship to a real error tracker
    // (Sentry, etc.) via a window.__errorReporter hook if present.
    console.error('ErrorBoundary caught:', error, info);
    this.setState({ info });
    if (typeof window !== 'undefined' && window.__errorReporter) {
      try { window.__errorReporter(error, info); } catch { /* noop */ }
    }
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (this.state.error) {
      const { variant = 'full-page' } = this.props;
      // Surface a request id if the error carries one (some of our
      // fetch wrappers attach reqId to thrown errors).
      const errorCode = this.state.error?.reqId
        ? `ERROR · 500 · ${this.state.error.reqId}`
        : 'ERROR · 500';
      return (
        <StateScreen
          variant={variant}
          kind="server-error"
          title="Something went wrong on our end"
          body="Our team has been notified. Give it a moment and try again — most issues resolve themselves quickly."
          primary={{ label: 'Try again', icon: 'refresh-cw', onClick: this.reset }}
          secondary={{ label: 'Reload page', icon: 'refresh-cw', onClick: () => window.location.reload() }}
          errorCode={errorCode}
        />
      );
    }
    return this.props.children;
  }
}
